/*
 * TMG Operations Wheel, application shell.
 * Entra members-only access (Cognito federated to Entra). There is one global
 * wheel and no multi-tenancy: no login form, no wheel list, no roles, no
 * user/tenant management. Authenticated members land directly on the wheel.
 */
import React, {Component, Suspense, lazy} from 'react';
import Navigation from './navigation';
import connect, {container} from 'react-redux-fetch';
import {Route, Switch, Redirect, BrowserRouter} from 'react-router-dom';
import '../styles.css';
import {CognitoUserPool, CognitoUser, CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken} from 'amazon-cognito-identity-js';
import {setAPIConfig} from '../util';

const Wheel = lazy(() => import('./tmg_wheel'));

const INITIAL_STATE = {cognitoUserPool: undefined, cognitoSession: undefined};
const TOKEN_KEYS = {ID_TOKEN: 'idToken', ACCESS_TOKEN: 'accessToken', REFRESH_TOKEN: 'refreshToken'};

// The single global wheel. Members always land here.
const TMG_WHEEL_ID = 'tmg-standup';
const TMG_WHEEL_PATH = `/app/wheel/${TMG_WHEEL_ID}`;
const WHEEL_ROUTE = '/app/wheel/:wheel_id';

class App extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
  }

  componentDidMount() {
    this.props.dispatchConfigGet();
  }

  componentDidUpdate() {
    if (this.state.cognitoUserPool === undefined && this.props.configFetch.fulfilled) {
      const config = this.props.configFetch.value;
      setAPIConfig(config);
      const cognitoUserPool = new CognitoUserPool({UserPoolId: config.UserPoolId, ClientId: config.ClientId});
      // Returning from the Entra Hosted UI redirect: capture the tokens.
      this.captureHostedUiTokens(cognitoUserPool, config);
      this.setState({cognitoUserPool}, () => {
        this.refreshSession();
        this.maybeRedirectToIdp(config);
      });
    }
  }

  // Parse an implicit-flow (#id_token=...) Hosted UI callback and seed the
  // Cognito session so getCurrentUser()/getSession() work as for a normal sign-in.
  captureHostedUiTokens = (pool) => {
    const hash = window.location.hash || '';
    if (hash.indexOf('id_token=') === -1) return false;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const idToken = params.get('id_token');
    if (!idToken) return false;
    const accessToken = params.get('access_token') || idToken;
    try {
      // JWT payloads are base64url, not plain base64: fix the alphabet and pad.
      let b64 = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      b64 += '='.repeat((4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(b64));
      const username = payload['cognito:username'] || payload.email || payload.sub;
      const session = new CognitoUserSession({
        IdToken: new CognitoIdToken({IdToken: idToken}),
        AccessToken: new CognitoAccessToken({AccessToken: accessToken}),
        RefreshToken: new CognitoRefreshToken({RefreshToken: params.get('refresh_token') || ''}),
      });
      const user = new CognitoUser({Username: username, Pool: pool});
      user.setSignInUserSession(session);
      localStorage.setItem(TOKEN_KEYS.ID_TOKEN, idToken);
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      try { sessionStorage.removeItem('tmg_sso_ts'); } catch (e) { /* ignore */ }
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      return true;
    } catch (e) {
      return false;
    }
  }

  idpUrl = (config) => {
    const redirectUri = config.RedirectUri || (window.location.origin + '/app');
    return `https://${config.CognitoDomain}/oauth2/authorize`
      + `?client_id=${encodeURIComponent(config.ClientId)}`
      + `&response_type=token`
      + `&scope=${encodeURIComponent('openid email profile')}`
      + `&redirect_uri=${encodeURIComponent(redirectUri)}`
      + `&identity_provider=${encodeURIComponent(config.IdentityProvider)}`;
  }

  // Frictionless SSO: forward to the Microsoft prompt when there is no session.
  maybeRedirectToIdp = (config) => {
    if (!config.IdentityProvider || !config.CognitoDomain) return;
    if (this._redirecting) return;
    if (this.state.cognitoUserPool && this.state.cognitoUserPool.getCurrentUser()) return;
    // Tight-loop guard only (20s): a normal reload re-forwards rather than
    // getting stuck on a sign-in screen.
    try {
      const last = Number(sessionStorage.getItem('tmg_sso_ts') || 0);
      if (Date.now() - last < 20000) return;
      sessionStorage.setItem('tmg_sso_ts', String(Date.now()));
    } catch (e) { /* proceed */ }
    this._redirecting = true;
    window.location.assign(this.idpUrl(config));
  }

  forceSignIn = () => {
    const config = this.props.configFetch.value || {};
    try { sessionStorage.removeItem('tmg_sso_ts'); } catch (e) { /* ignore */ }
    if (config.CognitoDomain && config.IdentityProvider) window.location.assign(this.idpUrl(config));
  }

  refreshSession = () => {
    const currentUser = this.state.cognitoUserPool.getCurrentUser();
    if (currentUser === null) return;
    const app = this;
    currentUser.getSession(function (err, session) {
      if (err) return;
      const idToken = session.getIdToken().getJwtToken();
      const currentIdToken = app.state.cognitoSession?.getIdToken()?.getJwtToken();
      if (currentIdToken !== idToken) {
        container.registerRequestHeader('Authorization', idToken);
        if (localStorage.getItem(TOKEN_KEYS.ID_TOKEN) !== idToken) {
          localStorage.setItem(TOKEN_KEYS.ID_TOKEN, idToken);
          localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, session.getAccessToken().getJwtToken());
          localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, session.getRefreshToken().getToken());
        }
        app.setState({cognitoSession: session});
      }
    });
  }

  userLogout = () => {
    const user = this.state.cognitoUserPool.getCurrentUser();
    if (user) user.signOut();
    try { sessionStorage.removeItem('tmg_sso_ts'); } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    this.setState(INITIAL_STATE);
  }

  childProps = () => ({
    userPool: new CognitoUserPool({
      UserPoolId: this.props.configFetch.value.UserPoolId,
      ClientId: this.props.configFetch.value.ClientId,
    }),
    userLogout: this.userLogout,
  });

  handleClickCapture = () => {
    if (this.state.cognitoUserPool !== undefined) this.refreshSession();
    else this.componentDidUpdate();
  }

  // Entra-only: never a username/password form. Auto-forward is in flight;
  // show a one-click fallback in case the guard suppressed it.
  renderSignIn = () => (
    <div style={{padding: 48, textAlign: 'center', fontFamily: 'system-ui'}}>
      <h2 style={{fontWeight: 600}}>TMG Operations Wheel</h2>
      <p style={{color: '#6b7280'}}>Redirecting you to TMG sign-in…</p>
      <button onClick={this.forceSignIn}
        style={{font: 'inherit', padding: '10px 18px', borderRadius: 9, border: 'none',
          background: '#4f8cff', color: '#fff', cursor: 'pointer', marginTop: 8}}>
        Sign in with Microsoft
      </button>
    </div>
  );

  render() {
    if (!this.props.configFetch.fulfilled) return <div style={{padding: 20}}>Loading…</div>;

    const authenticated = this.state.cognitoSession !== undefined && this.state.cognitoSession.isValid();
    if (!authenticated) return <BrowserRouter>{this.renderSignIn()}</BrowserRouter>;

    const childProps = this.childProps();
    return (
      <BrowserRouter>
        <div onClickCapture={this.handleClickCapture}>
          <Navigation {...childProps} />
          <Suspense fallback={<div style={{padding: 20, textAlign: 'center'}}>Loading…</div>}>
            <Switch>
              <Route path={WHEEL_ROUTE} exact={true} component={Wheel} />
              <Route path="/" exact={true} render={() => <Redirect to={TMG_WHEEL_PATH} />} />
              <Route path="/app" exact={true} render={() => <Redirect to={TMG_WHEEL_PATH} />} />
              <Route render={() => <Redirect to={TMG_WHEEL_PATH} />} />
            </Switch>
          </Suspense>
        </div>
      </BrowserRouter>
    );
  }
}

export default connect([
  {resource: 'config', method: 'get', request: {url: '/app/config.json'}},
])(App);
